import {
  OT_GUEST_ANONYMOUS_ID,
  OT_GUEST_CART_ID,
  OT_GUEST_CART_REF,
  OT_GUEST_MERGE_ATTEMPTS,
} from "src/utils/constants";
import {
  getStorageItem,
  removeStorageItem,
  setStorageItem,
} from "./localStorage";

/**
 * Guest-session hand-over.
 *
 * While a visitor shops as a guest we remember, in localStorage, the CT cart id
 * of their cart (only while it has line items) and the CT anonymousId of their
 * session. On the first member session those are sent to `mergeCart`: the cart
 * id merges the line items into the member cart, the anonymous id is recorded
 * on the customer so guest orders placed in that session can be linked to the
 * account. The keys are cleared only once the merge succeeds, or after
 * GUEST_MERGE_MAX_ATTEMPTS failed attempts.
 */

export const GUEST_MERGE_MAX_ATTEMPTS = 3;

export type GuestSession = {
  cartId: string | null;
  anonymousId: string | null;
};

export function rememberGuestCartRef(cartId: string): void {
  setStorageItem(OT_GUEST_CART_REF, cartId);
}

export function forgetGuestCartRef(): void {
  removeStorageItem(OT_GUEST_CART_REF);
}

export function rememberGuestAnonymousId(anonymousId: string): void {
  setStorageItem(OT_GUEST_ANONYMOUS_ID, anonymousId);
}

/** Drops the pre-STORE-1520 key, which is no longer written. */
export function removeLegacyGuestCartKey(): void {
  removeStorageItem(OT_GUEST_CART_ID);
}

export function readGuestSession(): GuestSession | null {
  const cartId = getStorageItem(OT_GUEST_CART_REF) || null;
  const anonymousId = getStorageItem(OT_GUEST_ANONYMOUS_ID) || null;

  if (!cartId && !anonymousId) {
    return null;
  }

  return { cartId, anonymousId };
}

export function clearGuestSession(): void {
  removeStorageItem(OT_GUEST_CART_REF);
  removeStorageItem(OT_GUEST_ANONYMOUS_ID);
  removeStorageItem(OT_GUEST_MERGE_ATTEMPTS);
}

/**
 * Records one failed merge attempt. Returns true when the cap is reached, in
 * which case the stored guest session is dropped so we stop retrying.
 */
export function registerGuestMergeFailure(): boolean {
  const attempts =
    (parseInt(getStorageItem(OT_GUEST_MERGE_ATTEMPTS) || "0", 10) || 0) + 1;

  if (attempts >= GUEST_MERGE_MAX_ATTEMPTS) {
    clearGuestSession();

    return true;
  }

  setStorageItem(OT_GUEST_MERGE_ATTEMPTS, String(attempts));

  return false;
}
